import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import CtaBlock from "@/components/CtaBlock";
import { Snowflake, Flame, Wind, Wrench, ArrowRight } from "lucide-react";

const CAT_ICONS = { "Air Conditioning": Snowflake, "Heating": Flame, "Heat Pumps": Wind, "Indoor Air Quality": Wind, "Ductwork": Wrench, "HVAC": Wrench };
const CAT_ORDER = ["Air Conditioning", "Heating", "Heat Pumps", "Indoor Air Quality", "Ductwork", "HVAC"];

export default function ServicesIndex() {
  const { data: services = [] } = useQuery({ queryKey: ["services-index"], queryFn: async () => (await api.get("/services")).data });
  const grouped = services.reduce((acc, s) => { (acc[s.category] = acc[s.category] || []).push(s); return acc; }, {});
  const cats = CAT_ORDER.filter((c) => grouped[c]);

  return (
    <>
      <Seo title="HVAC Services in Cocoa & the Space Coast | Aquila Air & Heating" description="Explore all HVAC services from Aquila Air & Heating — AC repair & installation, heating, heat pumps, indoor air quality, ductwork, and maintenance across Brevard County." path="/services/" />
      <section className="bg-navy text-white py-12 md:py-16" data-testid="services-index-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Services" }]} /></div>
          <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight">Our HVAC Services</h1>
          <p className="mt-3 max-w-2xl text-lg text-slate-200">Complete heating, cooling, and air quality solutions for every Space Coast home — repair, installation, replacement, and maintenance.</p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
          {cats.map((cat) => {
            const Icon = CAT_ICONS[cat] || Wrench;
            return (
              <div key={cat} data-testid={`services-cat-${cat}`}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="inline-flex items-center justify-center h-10 w-10 rounded-md bg-cool/10 text-cool"><Icon className="h-5 w-5" /></span>
                  <h2 className="text-2xl sm:text-3xl font-bold text-navy tracking-tight">{cat}</h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {grouped[cat].map((s) => (
                    <Link key={s.slug} to={`/${s.slug}`} data-testid={`services-item-${s.slug}`} aria-label={`Learn more about ${s.name}`} className="group bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                      <h3 className="text-lg font-semibold text-navy">{s.name}</h3>
                      <p className="mt-1.5 text-sm text-slate-600 line-clamp-2">{s.intro}</p>
                      <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-launch group-hover:gap-2 transition-all">Learn more <ArrowRight className="h-4 w-4" /></span>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <CtaBlock />
    </>
  );
}
